import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { usePageTitle } from "hooks/usePageTitle";
import { useToken } from "providers/token";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { parseSseFrame, parseStreamEventData } from "../../../util/sse";

type StreamStatus = "connecting" | "connected" | "closed" | "error";

type StreamEventRecord = {
  id: string;
  event: string;
  data: unknown;
};

function getApiBaseUrl() {
  return (
    import.meta.env.VITE_API_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:4000" : "")
  );
}

function useSseStream(
  tenantId: string | undefined,
  initialLastEventId: string | undefined,
  onEventId: (eventId: string) => void
) {
  const { getIdTokenRefreshIfExpired } = useToken();
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<StreamEventRecord[]>([]);
  const onEventIdRef = useRef(onEventId);

  useEffect(() => {
    onEventIdRef.current = onEventId;
  }, [onEventId]);

  useEffect(() => {
    if (!tenantId) {
      return;
    }

    const abortController = new AbortController();
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;

    setStatus("connecting");
    setError(null);
    setEvents([]);

    const appendEvent = (id: string, event: string, rawData: string) => {
      setEvents((previousEvents) => [
        {
          id,
          event,
          data: parseStreamEventData(rawData),
        },
        ...previousEvents,
      ]);

      onEventIdRef.current(id);
    };

    const processFrame = (frame: string) => {
      const parsedFrame = parseSseFrame(frame);
      if (!parsedFrame || !parsedFrame.id) {
        return;
      }

      appendEvent(parsedFrame.id, parsedFrame.event, parsedFrame.rawData);
    };

    const consumeStream = async () => {
      try {
        const idToken = await getIdTokenRefreshIfExpired();
        const headers: Record<string, string> = {
          accept: "text/event-stream",
          authorization: `Bearer ${idToken || ""}`,
        };

        if (initialLastEventId) {
          headers["Last-Event-ID"] = initialLastEventId;
        }

        const response = await fetch(`${getApiBaseUrl()}/events/${tenantId}/diagnostic`, {
          headers,
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to connect to SSE stream (${response.status}).`);
        }

        if (!response.body) {
          throw new Error("SSE response body was empty.");
        }

        setStatus("connected");
        reader = response.body.getReader();

        const decoder = new TextDecoder();
        let bufferedChunk = "";

        while (!abortController.signal.aborted) {
          const { done, value } = await reader.read();

          if (done) {
            const trailingChunk = `${bufferedChunk}${decoder.decode()}`.trim();
            if (trailingChunk) {
              processFrame(trailingChunk);
            }
            break;
          }

          bufferedChunk += decoder.decode(value, { stream: true });
          const frames = bufferedChunk.split(/\r?\n\r?\n/);
          bufferedChunk = frames.pop() ?? "";

          for (const frame of frames) {
            processFrame(frame);
          }
        }

        if (!abortController.signal.aborted) {
          setStatus("closed");
        }
      } catch (streamError) {
        if (abortController.signal.aborted) {
          return;
        }

        setStatus("error");
        setError(
          streamError instanceof Error ? streamError.message : "Failed to connect to SSE stream."
        );
      }
    };

    void consumeStream();

    return () => {
      abortController.abort();
      if (reader) {
        void reader.cancel().catch(() => undefined);
      }
    };
  }, [getIdTokenRefreshIfExpired, initialLastEventId, tenantId]);

  return {
    status: tenantId ? status : "error",
    error: tenantId ? error : "Missing tenant id.",
    events,
  };
}

export function Sse() {
  usePageTitle("SSE");

  const { tenantId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLastEventIdRef = useRef(searchParams.get("lastEventId") ?? undefined);
  const latestSearchRef = useRef(location.search);

  useEffect(() => {
    latestSearchRef.current = location.search;
  }, [location.search]);

  const handleEventId = useCallback(
    (eventId: string) => {
      const nextSearchParams = new URLSearchParams(latestSearchRef.current);

      if (nextSearchParams.get("lastEventId") === eventId) {
        return;
      }

      nextSearchParams.set("lastEventId", eventId);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [setSearchParams]
  );

  const { status, error, events } = useSseStream(
    tenantId,
    initialLastEventIdRef.current,
    handleEventId
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4">SSE</Typography>
            <Typography color="text.secondary">
              Live diagnostic events for tenant {tenantId ?? "unknown"}.
            </Typography>
          </Box>

          <Typography>Status: {status}</Typography>

          {error ? <Typography color="error">{error}</Typography> : null}

          <Divider />

          {events.length === 0 ? (
            <Typography color="text.secondary">No events received yet.</Typography>
          ) : (
            <List disablePadding>
              {events.map((streamEvent, index) => (
                <Box key={streamEvent.id}>
                  <ListItem alignItems="flex-start" disableGutters>
                    <ListItemText
                      primary={streamEvent.event}
                      secondaryTypographyProps={{ component: "div" }}
                      secondary={
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 1,
                            p: 2,
                            overflowX: "auto",
                            borderRadius: 1,
                            backgroundColor: "action.hover",
                            color: "text.primary",
                          }}
                        >
                          {JSON.stringify(streamEvent.data, null, 2)}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < events.length - 1 ? <Divider /> : null}
                </Box>
              ))}
            </List>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
